import { cn } from '@heidi/utils';

/** An absolutely positioned sibling inside a `group/landing relative` wrapper. */
export const workLandingComposerGlowClassName = cn(
  'pointer-events-none absolute -inset-8 rounded-[30px] blur-[5rem]',
  'bg-accent-yellow/10 transition-opacity duration-200 ease-out motion-reduce:transition-none',
  'group-focus-within/landing:bg-accent-yellow/[0.12]'
);

/**
 * The turn-in-flight counterpart to the static landing glow above. Where
 * `workLandingComposerGlowClassName` is a flat resting halo, this is the
 * ported animated "Heidi is thinking" glow: a bottom-anchored tail and core
 * layer that `ComposerAmbientGlow` drifts with `motion/react`. The two
 * share almost no styling vocabulary (geometry, blur, and colour all
 * differ), which is why they live as two exports of one module rather than
 * one export reused for both — see the spec's styling decision.
 *
 * Every host docks the composer against a clipping edge — about 2.5rem
 * below the card, 1rem beside it in the narrow session panel — so the
 * container is sized to that room, lit across the full card width, and
 * masked to nothing across that last 1rem at each side and down its
 * height. The layers can drift past it without a host ever cutting a lit
 * pixel.
 */
export const workTurnInFlightGlowClassNames = {
  container: cn(
    'pointer-events-none absolute -inset-x-4 -bottom-10 z-0 h-10 overflow-visible',
    '[mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%-2rem),transparent),linear-gradient(to_bottom,black_25%,transparent)] [mask-composite:intersect]',
    'transition-opacity duration-300 ease-out motion-reduce:transition-none'
  ),
  tail: cn(
    'absolute -inset-x-8 -bottom-4 top-0 rounded-[52%] blur-lg',
    'bg-gradient-to-r from-accent-skies/30 via-accent-skies/50 to-accent-skies/30'
  ),
  core: cn(
    'absolute -inset-x-16 bottom-2 top-0 origin-bottom rounded-[50%] blur-lg',
    'bg-gradient-to-r from-accent-skies/15 via-accent-skies/40 to-accent-skies/15'
  ),
} as const;
